/**
 * Per-location date-exceptions editor (DNKADM-0051 U2) — the editable list for
 * `GET/PUT /v2/admin/locations/{id}/hours/exceptions` (handoff §5.2), seeded from
 * the loaded list and saved through `putHoursExceptions`.
 *
 * Each exception is a date (`YYYY-MM-DD`, native `<TextField type="date">`) with an
 * "Open / Closed" toggle: a closed date overrides the weekly schedule (the location
 * is closed that day); an open date carries 1..n override shifts (same shift rules
 * as the weekly grid, `validateHours.ts`). Add a date, remove a date, edit its
 * shifts. A single **"Save exceptions"** action PUTs the whole list.
 *
 * Validation mirrors the server: an open exception needs ≥1 valid non-overlapping
 * shift; a duplicate date is rejected. A failing date shows its inline error and
 * BLOCKS Save. On success the parent `refetch`es + a success toast; a validation
 * `400` renders INLINE, anything else raises an error toast.
 *
 * Native MUI date/time inputs only — NO `@mui/x-date-pickers`.
 *
 * **Embedded in a drawer (DNKADM-0495).** With `embedded`, the editor renders no Save of its
 * own: the location page's Days closed drawer owns the one Save and drives it through
 * `handleRef`. Same validation, same whole-list PUT; a failure of either kind shows inline
 * (the drawer stays open on it) and the drawer raises the success toast.
 */
import { useImperativeHandle, useState, type Ref } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import type { HoursException, HoursShift } from '@/api/operatingHours';
import { putHoursExceptions } from '@/api/operatingHours';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';
import { validateExceptions, type ExceptionErrors } from './validateHours';

/** The drawer's handle on an embedded editor (DNKADM-0495). */
export interface HoursExceptionsEditorHandle {
  /** Validate and PUT the list. `true` = saved; `false` = not saved (the editor says why). */
  save: () => Promise<boolean>;
}

interface HoursExceptionsEditorProps {
  /** The loaded date exceptions for the location. */
  exceptions: HoursException[];
  /** The selected owned location's id, forwarded on PUT. */
  locationId: string;
  /** Re-fetch after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
  /** Render without a Save button; the owning drawer saves through `handleRef` (DNKADM-0495). */
  embedded?: boolean;
  /** The drawer's handle (embedded mode). */
  handleRef?: Ref<HoursExceptionsEditorHandle>;
}

/** A blank shift for a freshly-opened exception / an added shift row. */
function blankShift(): HoursShift {
  return { startTime: '', endTime: '' };
}

/** A new closed exception for today's date placeholder (empty = the operator fills it). */
function blankException(): HoursException {
  return { exceptionDate: '', isAvailable: false, shifts: [] };
}

export function HoursExceptionsEditor({
  exceptions: initial,
  locationId,
  refetch,
  showToast,
  embedded = false,
  handleRef,
}: HoursExceptionsEditorProps) {
  const [exceptions, setExceptions] = useState<HoursException[]>(initial);
  const [errors, setErrors] = useState<ExceptionErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function updateException(index: number, patch: Partial<HoursException>) {
    setExceptions((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }

  function handleToggleAvailable(index: number, available: boolean) {
    updateException(
      index,
      available
        ? { isAvailable: true, shifts: [blankShift()] }
        : { isAvailable: false, shifts: [] },
    );
  }

  function updateShift(index: number, shiftIndex: number, patch: Partial<HoursShift>) {
    setExceptions((prev) =>
      prev.map((e, i) =>
        i === index
          ? { ...e, shifts: e.shifts.map((s, si) => (si === shiftIndex ? { ...s, ...patch } : s)) }
          : e,
      ),
    );
  }

  function addShift(index: number) {
    setExceptions((prev) =>
      prev.map((e, i) => (i === index ? { ...e, shifts: [...e.shifts, blankShift()] } : e)),
    );
  }

  function removeShift(index: number, shiftIndex: number) {
    setExceptions((prev) =>
      prev.map((e, i) =>
        i === index ? { ...e, shifts: e.shifts.filter((_s, si) => si !== shiftIndex) } : e,
      ),
    );
  }

  function addException() {
    setExceptions((prev) => [...prev, blankException()]);
  }

  function removeException(index: number) {
    setExceptions((prev) => prev.filter((_e, i) => i !== index));
  }

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    // A blank date is incomplete — surface it before the shift-level checks.
    const missingDate = exceptions.some((e) => e.exceptionDate.trim() === '');
    if (missingDate) {
      setServerError('Every exception needs a date.');
      return false;
    }
    const result = validateExceptions(exceptions);
    if (!result.ok) {
      setErrors(result.errors);
      return false;
    }
    setErrors({});
    setSaving(true);
    try {
      await putHoursExceptions(exceptions, locationId);
      refetch();
      if (!embedded) showToast('Date exceptions saved', 'success');
      return true;
    } catch (err) {
      const view = classifyFormError(err, 'Those hours aren’t valid — check the highlighted days.', {
        toastFallback: 'The hours couldn’t be saved. Please try again.',
        conflictInline: false,
      });
      // Embedded: the drawer stays open on the failure, so it is said right here.
      if (view.kind === 'inline' || embedded) setServerError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(handleRef, () => ({ save: handleSave }));

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Paper elevation={1} sx={{ px: 2.75, py: exceptions.length === 0 ? 2.5 : 0.75 }}>
        {exceptions.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No date exceptions. Add one to close the location on a specific date or set special
            hours.
          </Typography>
        ) : (
          exceptions.map((exception, index) => (
            <ExceptionRow
              // Positional rows; index keys are correct (add/remove rebuilds the list).
              key={index}
              exception={exception}
              index={index}
              error={errors[exception.exceptionDate]}
              disabled={saving}
              last={index === exceptions.length - 1}
              onDateChange={(exceptionDate) => updateException(index, { exceptionDate })}
              onToggleAvailable={(available) => handleToggleAvailable(index, available)}
              onShiftChange={(si, patch) => updateShift(index, si, patch)}
              onAddShift={() => addShift(index)}
              onRemoveShift={(si) => removeShift(index, si)}
              onRemove={() => removeException(index)}
            />
          ))
        )}
      </Paper>
      {serverError && (
        <Box role="alert" sx={{ mt: 1.5, fontSize: 13, color: 'error.main', fontWeight: 500 }}>
          {serverError}
        </Box>
      )}
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mt: 1.75 }}
      >
        <Button
          size="small"
          startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
          onClick={addException}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          Add date exception
        </Button>
        {!embedded && (
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save exceptions'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

/** One exception's row: a date input, an Open/Closed toggle, and (when open) shifts. */
function ExceptionRow({
  exception,
  index,
  error,
  disabled,
  last,
  onDateChange,
  onToggleAvailable,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onRemove,
}: {
  exception: HoursException;
  index: number;
  error: string | undefined;
  disabled: boolean;
  last: boolean;
  onDateChange: (date: string) => void;
  onToggleAvailable: (available: boolean) => void;
  onShiftChange: (shiftIndex: number, patch: Partial<HoursShift>) => void;
  onAddShift: () => void;
  onRemoveShift: (shiftIndex: number) => void;
  onRemove: () => void;
}) {
  const t = resolveTokens(useTheme());
  const position = index + 1;
  return (
    <Box
      sx={{
        py: 2,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          type="date"
          label="Date"
          size="small"
          value={exception.exceptionDate}
          onChange={(e) => onDateChange(e.target.value)}
          disabled={disabled}
          error={Boolean(error)}
          sx={{ width: 180 }}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'aria-label': `Exception ${position} date` },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={exception.isAvailable}
              onChange={(e) => onToggleAvailable(e.target.checked)}
              disabled={disabled}
              slotProps={{ input: { 'aria-label': `Exception ${position} open` } }}
            />
          }
          label={exception.isAvailable ? 'Open' : 'Closed'}
        />
        <Box sx={{ flex: 1 }} />
        <IconAction
          size="small"
          label={`Remove exception ${position}`}
          onClick={onRemove}
          disabled={disabled}
        >
          <Icon name="delete" sx={{ fontSize: 20 }} />
        </IconAction>
      </Stack>

      {exception.isAvailable && (
        <Box sx={{ mt: 1, pl: { sm: 0 } }}>
          {exception.shifts.map((shift, si) => (
            <Stack
              key={si}
              direction="row"
              sx={{ alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}
            >
              <TextField
                type="time"
                label="Start"
                size="small"
                value={shift.startTime}
                onChange={(e) => onShiftChange(si, { startTime: e.target.value })}
                disabled={disabled}
                error={Boolean(error)}
                sx={{ width: 140 }}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    'aria-label': `Exception ${position} shift ${si + 1} start time (HH:mm, local)`,
                  },
                }}
              />
              <TextField
                type="time"
                label="End"
                size="small"
                value={shift.endTime}
                onChange={(e) => onShiftChange(si, { endTime: e.target.value })}
                disabled={disabled}
                error={Boolean(error)}
                sx={{ width: 140 }}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: {
                    'aria-label': `Exception ${position} shift ${si + 1} end time (HH:mm, local)`,
                  },
                }}
              />
              <IconAction
                size="small"
                label={`Remove exception ${position} shift ${si + 1}`}
                onClick={() => onRemoveShift(si)}
                disabled={disabled || exception.shifts.length === 1}
              >
                <Icon name="close" sx={{ fontSize: 18 }} />
              </IconAction>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
            onClick={onAddShift}
            disabled={disabled}
            sx={{ textTransform: 'none' }}
          >
            Add shift
          </Button>
        </Box>
      )}

      {error && (
        <Typography role="alert" sx={{ mt: 0.75, fontSize: 12, color: 'error.main' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
